import {
  IonCol,
  IonGrid,
  IonItem,
  IonList,
  IonPopover,
  IonRow,
  IonSearchbar,
  IonSpinner,
  IonText,
} from '@ionic/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import './Tracks.scss';
import { iTrack } from '../../types/ITrack';
import { useDispatch } from '../../store/hooks';
import { useHistory } from 'react-router-dom';
import {
  fetchTrackById,
  setSelectedTrack,
} from '../../store/slices/track.slice';
import { CrateService } from '../../services/Crate.service';
import {
  addTrackToCrate,
  fetchCrateById,
  saveCrateChanges,
  setSelectedCrate,
} from '../../store/slices/crate.slice';
import { useToast } from '../ToastManager/useToast';
import CrateUpdateService from '../../services/CrateUpdateService';

interface CondensedTrackViewProps extends iTrack {
  handleClose: () => void;
}

export const CondensedTrackView: React.FC<CondensedTrackViewProps> = ({
  handleClose,
  ...track
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [showPopover, setShowPopover] = useState(false);
  const [searchText, setSearchText] = useState('');
  const popoverRef = useRef<HTMLIonPopoverElement>(null);
  const [crates, setCrates] = useState<Array<any>>([]);
  const { successToast, errorToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const onTrackClicked = () => {
    dispatch(setSelectedTrack(track));
    history.push('/track');
    handleClose();
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPopover(true);
  };

  // Fetch crates using CrateService
  const getMyCrates = useCallback(() => {
    setIsLoading(true);
    CrateService.fetchSelectedCrates(
      [CrateService.MY_CRATES],
      (fetchedCrates) => {
        setCrates(fetchedCrates);
        setIsLoading(false);
      },
      undefined,
      history,
    );
  }, [history]);

  useEffect(() => {
    if (showPopover) {
      getMyCrates();
    }
  }, [showPopover, getMyCrates]);

  const filteredCrates = crates.filter((crate) =>
    crate.Title?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleAddTrackToCrate = async (crate: any) => {
    try {
      // First, fetch the complete crate data by ID
      const crateResult = await dispatch(fetchCrateById(crate._id));
      if (fetchCrateById.fulfilled.match(crateResult) && crateResult.payload) {
        const fullCrateData = crateResult.payload;

        const trackInCrate = fullCrateData.Tracks?.find(
          (crateTrack: any) => crateTrack.SpotifyID === track.ID,
        );

        // Avoid adding a track that is already in the crate
        if (trackInCrate) {
          errorToast({
            message: `${trackInCrate.Title} already exists in ${fullCrateData.Title}`,
          });
          setShowPopover(false);
          return;
        }

        // Set the selected crate with the full data we fetched
        dispatch(setSelectedCrate(fullCrateData));

        // Fetch full track data
        const trackResult = await dispatch(fetchTrackById(track.ID));

        if (
          fetchTrackById.fulfilled.match(trackResult) &&
          trackResult.payload
        ) {
          await dispatch(addTrackToCrate(trackResult.payload));
          await dispatch(saveCrateChanges());

          CrateUpdateService.notifyCrateUpdated(crate._id);

          setShowPopover(false);
          successToast({
            message: `Added new track to ${crate?.Title}`,
          });
        }
      }
    } catch (error) {
      console.error('Error adding track:', error);
      errorToast({ message: 'Unable to add track' });
      setShowPopover(false);
    }
  };

  return (
    <>
      <IonItem
        lines="none"
        className="condensed-track-view"
        onClick={onTrackClicked}
        onContextMenu={handleContextMenu}
      >
        <IonGrid>
          <IonRow>
            <IonCol size="3" className="ion-col-condensed">
              <img
                className="condensed-track-image"
                src={track.AlbumCover}
                alt={track.Title}
              />
            </IonCol>
            <IonCol size="8">
              <IonGrid>
                <IonCol className="condensed-track-text-container">
                  <IonRow>
                    <IonText className="condensed-track-title">
                      {track.Title}
                    </IonText>
                  </IonRow>
                  <IonRow>
                    <IonText className="condensed-track-artist">
                      {track.Artist}
                    </IonText>
                  </IonRow>
                </IonCol>
              </IonGrid>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonItem>
      <IonPopover
        ref={popoverRef}
        isOpen={showPopover}
        onDidDismiss={() => setShowPopover(false)}
        className="spotify-style-popover"
      >
        <div className="crate-popover-container">
          <div className="popover-header">
            <div className="header-main">
              <span>ADD TO CRATE</span>
            </div>
            <IonSearchbar
              value={searchText}
              onIonInput={(e) => setSearchText(e.detail.value || '')}
              placeholder="Find a crate"
              className="spotify-searchbar"
            />
          </div>
          <IonList className="crate-list">
            {isLoading ? (
              <div className="loading-container">
                <IonSpinner name="crescent" />
                <IonText color="medium">Loading crates...</IonText>
              </div>
            ) : filteredCrates.length > 0 ? (
              filteredCrates.map((crate) => (
                <IonItem
                  key={crate.id}
                  button
                  onClick={() => handleAddTrackToCrate(crate)}
                  className="crate-item"
                >
                  <div className="crate-item-content">
                    <IonText>
                      <h4>{crate.Title}</h4>
                    </IonText>
                    <IonText color="medium">
                      <p className="track-count">
                        Tracks: {crate.trackCount ?? 0}
                      </p>
                    </IonText>
                  </div>
                </IonItem>
              ))
            ) : searchText.length > 0 ? (
              <IonItem lines="none" className="no-results">
                <IonText color="medium">No matching crates found</IonText>
              </IonItem>
            ) : (
              <IonItem lines="none" className="no-results">
                <IonText color="medium">No crates available</IonText>
              </IonItem>
            )}
          </IonList>
        </div>
      </IonPopover>
    </>
  );
};
